import { useFormikContext } from 'formik'
import { useApolloClient } from '@apollo/client'
import { useIntl } from 'react-intl'
import { useUserSession } from '@src/utils/useUserSession'
import {
  SemiConfigFormValues,
  SearchResultType,
  useActiveStepFromQueryParam,
  useProductFromQueryParam,
  getInitialValuesBasedOnProduct,
} from './ProductsConfiguration'
import {
  SemiConfigSearchQuery,
  SemiConfigSearchDocument,
} from '@src/queries/SemiConfiguratorCrisprgrnaSearchQuery.generated'
import {
  ShrnaSearchTermQuery,
  ShrnaSearchTermDocument,
} from '@src/queries/ShrnaProductSearchQuery.generated'
import { ActiveLinkType, SemiConfigProductTypes } from './enum'
import {
  OrfSearchTermDocument,
  OrfSearchTermQuery,
} from '@src/queries/OrfProductSearchQuery.generated'
import {
  SangerSearchTermDocument,
  SangerSearchTermQuery,
} from '@src/queries/SangerCrisprProductSearchQuery.generated'

interface searchQueryProps {
  searchKey?: string
  isUpload?: boolean
  facet?: string[]
  count?: string
  sort?: string
  order?: string
  page?: number
}
const useSearch = () => {
  const formik = useFormikContext<SemiConfigFormValues>()
  const client = useApolloClient()
  const product = useProductFromQueryParam()
  const { userSession } = useUserSession()
  const activeStep = useActiveStepFromQueryParam()
  const intl = useIntl()
  const initialValues = getInitialValuesBasedOnProduct(product)
  const searchQuery = async ({
    searchKey,
    isUpload,
    facet,
    count,
    sort,
    order,
    page,
  }: searchQueryProps): Promise<SearchResultType | boolean | string> => {
    const searchTerm = searchKey || formik.values.searchData.searchKey
    const formattedSearchTerm = searchTerm
      .trim()
      .replace(/[/\n+\s]/g, ',')
      .replace(/,+/g, ',')
    const errorMessage = `${intl.formatMessage({ id: 'CONF_MANUAL_SEARCH_1_ERROR_POPUP_crispgrna', defaultMessage: 'We are sorry, but your search query "' })}${searchTerm}${intl.formatMessage({ id: 'CONF_MANUAL_SEARCH_2_ERROR_POPUP_crispgrna', defaultMessage: '" returned no results. Please review your query and try again.' })}`

    try {
      if (activeStep === ActiveLinkType.ProductSearch) {
        const searchTerm = searchKey || formik.values.searchData.searchKey
        !isUpload &&
          formik.setFieldValue(
            'searchData',
            { ...initialValues.searchData, searchKey: searchTerm },
            false
          )

        if (searchTerm === '') {
          formik.setFieldValue('completedStep', 0, false)
          if (isUpload) {
            return intl.formatMessage({
              id: 'CONF_REQUIRED_FIELD',
              defaultMessage: 'Required Field',
            })
          } else {
            formik.setFieldValue(
              'searchData.searchErrorMessage',
              intl.formatMessage({
                id: 'CONF_REQUIRED_FIELD',
                defaultMessage: 'Required Field',
              }),
              false
            )
            return false
          }
        } else {
          //check for query string length //if greater than 100, throw error
          if (formattedSearchTerm.split(',').length > 100) {
            formik.setFieldValue('completedStep', 0, false)
            if (isUpload) {
              return intl.formatMessage({
                id: 'CONF_UPLOAD_ERROR_MSG',
                defaultMessage: 'Maximum number of items per session is 100.',
              })
            } else {
              formik.setFieldValue(
                'searchData.searchErrorMessage',
                intl.formatMessage({
                  id: 'CONF_UPLOAD_ERROR_MSG',
                  defaultMessage: 'Maximum number of items per session is 100.',
                }),
                false
              )
              return false
            }
          } else
            formik.setFieldValue('searchData.searchErrorMessage', '', false)
        }
      }
      formik.setFieldValue('loading', true, false)
      const facetValue = isUpload
        ? initialValues.searchData.facet
        : facet || formik.values.searchData.facet
      let data
      const variables = {
        input: {
          term: formattedSearchTerm,
          count: parseInt(
            isUpload
              ? initialValues.searchData.count
              : count || formik.values.searchData.count
          ),
          page: isUpload
            ? initialValues.searchData.page
            : page || formik.values.searchData.page,
          facet: facetValue.length > 0 ? facetValue : ['species: NONE'],
          sort: isUpload
            ? initialValues.searchData.sort
            : sort || formik.values.searchData.sort,
          order: isUpload
            ? initialValues.searchData.order
            : order || formik.values.searchData.order,
          region: userSession.country,
          locale: `${userSession.language}_${userSession.country}`,
          store: userSession.store,
        },
      }
      if (product === SemiConfigProductTypes.PREDESIGNED_SHRNA) {
        const result = await client.query<ShrnaSearchTermQuery>({
          query: ShrnaSearchTermDocument,
          variables,
        })
        data = result.data.getShrnaSearchTerm
      } else if (product === SemiConfigProductTypes.ORF) {
        const result = await client.query<OrfSearchTermQuery>({
          query: OrfSearchTermDocument,
          variables,
        })
        data = result.data.getOrfSearchTerm
      } else if (product === SemiConfigProductTypes.SANGERCRISPR) {
        const result = await client.query<SangerSearchTermQuery>({
          query: SangerSearchTermDocument,
          variables,
        })
        data = result.data.getSangerSearchTerm
      } else {
        const result = await client.query<SemiConfigSearchQuery>({
          query: SemiConfigSearchDocument,
          variables,
        })
        data = result.data.getSemiConfigSearch
      }
      formik.setFieldValue('loading', false, false)
      //if empty result, display error message
      if (
        (product === SemiConfigProductTypes.PREDESIGNED_CRISPR_GRNA &&
          !(Array.isArray(data?.controls) && data?.controls?.length > 0)) ||
        (product !== SemiConfigProductTypes.PREDESIGNED_CRISPR_GRNA &&
          !data?.count)
      ) {
        if (isUpload && searchTerm) {
          return errorMessage
        } else {
          searchTerm &&
            activeStep === ActiveLinkType.ProductSearch &&
            formik.setFieldValue(
              'searchData.searchErrorMessage',
              errorMessage,
              false
            )
          return false
        }
      } else {
        if (
          isUpload &&
          data?.count > 0 &&
          Array.isArray(data?.term) &&
          data?.term.length > 0
        )
          formik.setFieldValue(
            'searchData.searchKey',
            data?.term.join(','),
            false
          )
        return {
          controls: data?.controls || [],
          reagents: data?.reagents || [],
          count: data?.count,
          term: data?.term || [],
        }
      }
    } catch (err) {
      formik.setFieldValue('loading', false, false)
      //display error message
      searchTerm &&
        activeStep === ActiveLinkType.ProductSearch &&
        formik.setFieldValue(
          'searchData.searchErrorMessage',
          errorMessage,
          false
        )
      return false
    }
  }
  return {
    searchQuery,
  }
}

export default useSearch
